/**
 * Offline Database Entry Point
 * 
 * Manages the SQLite WASM Web Worker lifecycle and provides
 * a typed API for the rest of the app to interact with the local cache.
 * Uses Comlink for seamless main thread ↔ worker communication.
 */

'use client';

import * as Comlink from 'comlink';
import type { SQLiteWorkerAPI } from './sqlite-worker';

let workerInstance: Worker | null = null;
let dbProxy: Comlink.Remote<SQLiteWorkerAPI> | null = null;
let initPromise: Promise<boolean> | null = null;
let isInitialized = false;

/**
 * Get or create the SQLite WASM worker instance.
 * Returns null if initialization fails — app continues online-only.
 */
export async function getOfflineDB(): Promise<Comlink.Remote<SQLiteWorkerAPI> | null> {
  if (dbProxy && isInitialized) return dbProxy;

  if (initPromise) {
    await initPromise;
    return dbProxy; // may be null if init failed
  }

  initPromise = (async () => {
    try {
      // Create worker
      workerInstance = new Worker(
        new URL('./sqlite-worker.ts', import.meta.url),
        { type: 'module' }
      );

      dbProxy = Comlink.wrap<SQLiteWorkerAPI>(workerInstance);

      // Initialize the database (creates file, runs migrations)
      // Pass origin from main thread — Worker's self.location may be unreliable
      const success = await dbProxy.initDB(window.location.origin);
      if (!success) {
        console.warn('[OfflineDB] Worker initialization failed — running online-only');
        dbProxy = null;
        return false;
      }

      isInitialized = true;
      console.log('[OfflineDB] Ready');
      return true;
    } catch (error) {
      console.warn('[OfflineDB] Failed to start worker — running online-only:', error);
      dbProxy = null;
      return false;
    }
  })();

  await initPromise;
  return dbProxy; // null if failed, proxy if success
}

/**
 * Check if offline DB is available and initialized
 */
export function isOfflineDBReady(): boolean {
  return isInitialized && dbProxy !== null;
}

/**
 * Get database stats (table row counts)
 */
export async function getOfflineDBStats(): Promise<Record<string, number>> {
  try {
    const db = await getOfflineDB();
    return await db.getStats();
  } catch {
    return {};
  }
}

// ============= Typed Query Helpers =============

/**
 * Cache drugs to SQLite
 */
export async function cacheDrugs(drugs: Array<{
  id: string;
  name: string;
  unit: string;
  stock: number;
  price: number;
  image?: string;
  brand?: string;
  barcode?: string;
  defaultUsageUnit?: string;
  defaultUsageInstruction?: string;
  earliestExpiryDate?: string;
}>): Promise<void> {
  try {
    const db = await getOfflineDB();
    if (!db) return;
    const now = Date.now();
    const columns = ['id', 'name', 'unit', 'stock', 'price', 'image', 'brand', 'barcode', 'default_usage_unit', 'default_usage_instruction', 'earliest_expiry_date', 'synced_at'];
    const records = drugs.map(d => [
      d.id, d.name, d.unit, d.stock, d.price,
      d.image || null, d.brand || null, d.barcode || null,
      d.defaultUsageUnit || null, d.defaultUsageInstruction || null,
      d.earliestExpiryDate || null,
      now,
    ]);
    await db.bulkUpsert('drugs', columns, records);
    
    // Update sync metadata
    await db.execute(
      `INSERT OR REPLACE INTO sync_meta (table_name, last_synced_at, record_count) VALUES (?, ?, ?)`,
      ['drugs', now, drugs.length]
    );
    
    console.log(`[OfflineDB] Cached ${drugs.length} drugs`);
  } catch (error) {
    console.error('[OfflineDB] Failed to cache drugs:', error);
  }
}

/**
 * Get drugs from local cache
 */
export async function getCachedDrugs(search?: string, limit = 50): Promise<any[]> {
  try {
    const db = await getOfflineDB();
    if (!db) return [];
    if (search && search.length >= 2) {
      const rows = await db.query(
        `SELECT * FROM drugs WHERE name LIKE ? ORDER BY name ASC LIMIT ?`,
        [`%${search}%`, limit]
      );
      return rows.map((r: any) => ({ ...r, earliestExpiryDate: r.earliest_expiry_date, defaultUsageUnit: r.default_usage_unit, defaultUsageInstruction: r.default_usage_instruction }));
    }
    const rows = await db.query(`SELECT * FROM drugs ORDER BY name ASC LIMIT ?`, [limit]);
    return rows.map((r: any) => ({ ...r, earliestExpiryDate: r.earliest_expiry_date, defaultUsageUnit: r.default_usage_unit, defaultUsageInstruction: r.default_usage_instruction }));
  } catch {
    return [];
  }
}

/**
 * Cache patients to SQLite
 */
export async function cachePatientsToDB(patients: Array<{
  id: string;
  patientCode?: string;
  name: string;
  gender?: string | null;
  birthDate?: string;
  age?: number;
  weight?: number;
  phone?: string;
  address?: string;
}>): Promise<void> {
  try {
    const db = await getOfflineDB();
    if (!db) return;
    const now = Date.now();
    const columns = ['id', 'patient_code', 'name', 'gender', 'birth_date', 'age', 'weight', 'phone', 'address', 'synced_at'];
    const records = patients.map(p => [
      p.id, p.patientCode || null, p.name, p.gender || null,
      p.birthDate || null, p.age || null, p.weight || null,
      p.phone || null, p.address || null, now,
    ]);
    await db.bulkUpsert('patients', columns, records);
    
    await db.execute(
      `INSERT OR REPLACE INTO sync_meta (table_name, last_synced_at, record_count) VALUES (?, ?, ?)`,
      ['patients', now, patients.length]
    );
    
    console.log(`[OfflineDB] Cached ${patients.length} patients`);
  } catch (error) {
    console.error('[OfflineDB] Failed to cache patients:', error);
  }
}

/**
 * Get patients from local cache
 */
export async function getCachedPatientsFromDB(search?: string, limit = 2000): Promise<any[]> {
  try {
    const db = await getOfflineDB();
    if (!db) return [];
    if (search && search.trim()) {
      const q = `%${search.toLowerCase()}%`;
      return await db.query(
        `SELECT * FROM patients WHERE LOWER(name) LIKE ? OR patient_code LIKE ? OR phone LIKE ? ORDER BY name ASC LIMIT ?`,
        [q, q, q, limit]
      );
    }
    return await db.query(`SELECT * FROM patients ORDER BY name ASC LIMIT ?`, [limit]);
  } catch {
    return [];
  }
}

/**
 * Cache queue to SQLite
 */
export async function cacheQueueToDB(queueItems: any[]): Promise<void> {
  try {
    const db = await getOfflineDB();
    if (!db) return;
    const now = Date.now();
    
    // Clear old queue data (queue is daily)
    await db.clearTable('queue');
    
    const columns = [
      'visit_id', 'patient_id', 'patient_code', 'name', 'age', 'birth_date',
      'weight', 'gender', 'phone', 'address', 'status', 'waiting_mins',
      'notes', 'diagnosis', 'allergies', 'service_name', 'service_code',
      'mode', 'ordered_services', 'synced_at'
    ];
    const records = queueItems.map(q => [
      q.visitId || q.id, q.patientId || null, q.patientCode || null,
      q.name, q.age || null, q.birthDate || null,
      q.weight || null, q.gender || null, q.phone || null, q.address || null,
      q.status || 'waiting', q.waitingMins || 0,
      // The local `notes` column has always held the chief complaint; the queue
      // payload just stopped calling it `notes`. Keeping the column name avoids an
      // offline-store migration for a disposable cache.
      q.chiefComplaint || null, q.diagnosis || null,
      q.allergies ? JSON.stringify(q.allergies) : null,
      q.serviceName || null, q.serviceCode || null,
      q.mode || null,
      q.orderedServices ? JSON.stringify(q.orderedServices) : null,
      now,
    ]);
    
    if (records.length > 0) {
      await db.bulkUpsert('queue', columns, records, 'visit_id');
    }
    console.log(`[OfflineDB] Cached ${queueItems.length} queue items`);
  } catch (error) {
    console.error('[OfflineDB] Failed to cache queue:', error);
  }
}

/**
 * Get queue from local cache
 */
export async function getCachedQueueFromDB(): Promise<any[]> {
  try {
    const db = await getOfflineDB();
    if (!db) return [];
    const rows = await db.query(`SELECT * FROM queue ORDER BY waiting_mins DESC`);
    // Parse JSON fields back
    return rows.map((r: any) => ({
      ...r,
      visitId: r.visit_id,
      patientId: r.patient_id,
      patientCode: r.patient_code,
      birthDate: r.birth_date,
      waitingMins: r.waiting_mins,
      serviceName: r.service_name,
      serviceCode: r.service_code,
      orderedServices: r.ordered_services ? JSON.parse(r.ordered_services) : undefined,
      allergies: r.allergies ? JSON.parse(r.allergies) : undefined,
    }));
  } catch {
    return [];
  }
}

/**
 * Cache settings to SQLite
 */
export async function cacheSettingsToDB(settingsArr: Array<{ key: string; value: string }>): Promise<void> {
  try {
    const db = await getOfflineDB();
    if (!db) return;
    const now = Date.now();
    const columns = ['key', 'value', 'synced_at'];
    const records = settingsArr.map(s => [s.key, s.value, now]);
    await db.bulkUpsert('settings', columns, records, 'key');
    console.log(`[OfflineDB] Cached ${settingsArr.length} settings`);
  } catch (error) {
    console.error('[OfflineDB] Failed to cache settings:', error);
  }
}

/**
 * Get settings from local cache
 */
export async function getCachedSettingsFromDB(): Promise<Array<{ key: string; value: string }> | null> {
  try {
    const db = await getOfflineDB();
    if (!db) return null;
    const rows = await db.query(`SELECT key, value FROM settings`);
    return rows.length > 0 ? rows : null;
  } catch {
    return null;
  }
}

// ============= Prescriptions Cache =============

/**
 * Cache prescriptions to SQLite
 */
export async function cachePrescriptionsToDB(prescriptions: Array<{
  id: string;
  sotoa?: string;
  ngay?: string;
  mabs?: string;
  sonv?: string;
  loidan?: string;
  taikham?: string;
  doctorName?: string;
  patientId?: string;
  patientName?: string;
  totalEstimate?: number;
  items?: any[];
}>): Promise<void> {
  try {
    const db = await getOfflineDB();
    if (!db) return;
    const now = Date.now();
    const columns = [
      'id', 'sotoa', 'ngay', 'mabs', 'sonv', 'loidan', 'taikham',
      'doctor_name', 'patient_id', 'patient_name', 'total_estimate', 'items', 'synced_at'
    ];
    const records = prescriptions.map(p => [
      p.id, p.sotoa || null, p.ngay || null, p.mabs || null, p.sonv || null,
      p.loidan || null, p.taikham || null, p.doctorName || null,
      p.patientId || null, p.patientName || null, p.totalEstimate || 0,
      p.items ? JSON.stringify(p.items) : null, now,
    ]);
    await db.bulkUpsert('prescriptions', columns, records);

    await db.execute(
      `INSERT OR REPLACE INTO sync_meta (table_name, last_synced_at, record_count) VALUES (?, ?, ?)`,
      ['prescriptions', now, prescriptions.length]
    );

    console.log(`[OfflineDB] Cached ${prescriptions.length} prescriptions`);
  } catch (error) {
    console.error('[OfflineDB] Failed to cache prescriptions:', error);
  }
}

/**
 * Get prescriptions from local cache (paginated)
 */
export async function getCachedPrescriptionsFromDB(
  page = 1,
  pageSize = 20
): Promise<{ data: any[]; total: number }> {
  try {
    const db = await getOfflineDB();
    if (!db) return { data: [], total: 0 };

    const countResult = await db.query(`SELECT COUNT(*) as cnt FROM prescriptions`);
    const total = countResult[0]?.cnt || 0;

    const offset = (page - 1) * pageSize;
    const rows = await db.query(
      `SELECT * FROM prescriptions ORDER BY ngay DESC LIMIT ? OFFSET ?`,
      [pageSize, offset]
    );

    // Parse JSON items field back
    const data = rows.map((r: any) => ({
      id: r.id,
      rowKey: r.id,
      sotoa: r.sotoa,
      ngay: r.ngay,
      mabs: r.mabs,
      sonv: r.sonv,
      loidan: r.loidan,
      taikham: r.taikham,
      doctorName: r.doctor_name,
      patientId: r.patient_id,
      patientName: r.patient_name,
      totalEstimate: r.total_estimate,
      isMiddleware: true,
      items: r.items ? JSON.parse(r.items) : [],
    }));

    return { data, total };
  } catch {
    return { data: [], total: 0 };
  }
}

// ============= Write Queue =============

/**
 * Queue a mutation for offline replay
 */
export async function queueMutation(
  endpoint: string,
  method: string,
  body: string
): Promise<string | null> {
  try {
    const db = await getOfflineDB();
    if (!db) return null;
    const id = crypto.randomUUID();
    const now = Date.now();
    await db.execute(
      `INSERT INTO write_queue (id, endpoint, method, body, status, retries, created_at) VALUES (?, ?, ?, ?, 'pending', 0, ?)`,
      [id, endpoint, method, body, now]
    );
    console.log(`[WriteQueue] Queued: ${method} ${endpoint} (${id})`);
    return id;
  } catch (error) {
    console.error('[WriteQueue] Failed to queue mutation:', error);
    return null;
  }
}

/**
 * Get all pending mutations for replay
 */
export async function getPendingMutations(): Promise<any[]> {
  try {
    const db = await getOfflineDB();
    if (!db) return [];
    return await db.query(
      `SELECT * FROM write_queue WHERE status IN ('pending', 'failed') ORDER BY created_at ASC`
    );
  } catch {
    return [];
  }
}

/**
 * Update mutation status after replay attempt
 */
export async function updateMutationStatus(
  id: string,
  status: 'syncing' | 'done' | 'failed',
  error?: string
): Promise<void> {
  try {
    const db = await getOfflineDB();
    if (!db) return;
    const now = Date.now();
    if (status === 'done') {
      await db.execute(
        `UPDATE write_queue SET status = ?, synced_at = ? WHERE id = ?`,
        [status, now, id]
      );
    } else {
      await db.execute(
        `UPDATE write_queue SET status = ?, retries = retries + 1, error = ? WHERE id = ?`,
        [status, error || null, id]
      );
    }
  } catch (error) {
    console.error('[WriteQueue] Failed to update status:', error);
  }
}

/**
 * Get count of pending mutations
 */
export async function getPendingMutationCount(): Promise<number> {
  try {
    const db = await getOfflineDB();
    if (!db) return 0;
    const result = await db.query(
      `SELECT COUNT(*) as cnt FROM write_queue WHERE status IN ('pending', 'failed')`
    );
    return result[0]?.cnt || 0;
  } catch {
    return 0;
  }
}

/**
 * Clear completed mutations older than 1 hour
 */
export async function clearCompletedMutations(): Promise<void> {
  try {
    const db = await getOfflineDB();
    if (!db) return;
    const cutoff = Date.now() - 60 * 60 * 1000;
    await db.execute(
      `DELETE FROM write_queue WHERE status = 'done' AND synced_at < ?`,
      [cutoff]
    );
  } catch {
    // Ignore
  }
}

